import React, { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { Component } from "../../types/ContentStructure";
import { Box, Container, Typography } from "@mui/material";
import ComponentLink from "./ComponentLink";
import { generateColors, generateLinearGradient } from "../../utils/utils";
import CssFilterConverter from "css-filter-converter";
import { opacity } from 'html2canvas/dist/types/css/property-descriptors/opacity';

interface IComponentCarruselProps {
    component: Component;
    sx?: any;
    mini?: boolean; // modo compacto para grid 2 cols mobile
}

const ComponentCarrusel: FC<IComponentCarruselProps> = ({ component, sx, mini }) => {
    const themeSys = useTheme();
    const { theme, title, subTitle, text, icon, components } = component;

    const bgMd = component.image;

    const color_filter = CssFilterConverter.hexToFilter(theme.themeStyles.backgroundColor || '#FFF');
    const { lightCss } = generateColors(theme.themeStyles.backgroundColor || '#FFF', 30);
    const gradient = generateLinearGradient(theme.themeStyles.backgroundColor || '#000a2d', 30);
    const bgColor = theme.themeStyles.backgroundColor || '#000a2d';

    // ── MODO MINI: tarjeta compacta para grid 2 columnas en mobile ────────────
    if (mini) {
        return (
            <Box sx={[
                theme.themeStyles,
                {
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    overflow: 'hidden',
                    //background: gradient,
                    borderRadius: 3,
                    m: 1,
                    aspectRatio: '1/1',
                },
                sx
            ]}>
                {/* Ícono decorativo */}

                {icon &&
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        zIndex: 0,
                        overflow: 'hidden',
                        userSelect: 'none'
                    }}>
                        <img
                            src={icon || ''}
                            alt=""
                            draggable={false}
                            style={{
                                width: 'auto', height: '100%',
                                userSelect: 'none',
                                pointerEvents: 'none'
                            }}
                        />
                    </Box>
                }
                {/* Imagen de fondo */}

                {/* Texto + CTA */}
                <Box sx={{
                    height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1, p: 1.5, pb: 2,
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {title && (
                        <Typography variant="h3" sx={{}}>
                            {title}
                        </Typography>
                    )}
                    {subTitle && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mb: 1 }}>
                            {subTitle}
                        </Typography>
                    )}
                    {/* {text && (
                        <Typography
                            sx={{ color: '#FFFE', display: 'block', mb: 1, fontSize: '0.475rem' }}>
                            {text}
                        </Typography>
                    )} */}
                    {components.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {components.map((item: Component, idx: number) => (
                                <ComponentLink key={idx} component={item} variant="cta" sx={{ opacity: '0.8', transform: 'scale(0.8)' }} />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }

    // ── MODO NORMAL: Desktop / Tablet ───────────────────────────────────────
    return (
        <Box sx={[
            {
                width: '100%',
                height: '100%',
                p: { xs: 2, md: 8 },
            },
        ]}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 0, md: 0, lg: 0 } }}>
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'row',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                    overflow: 'hidden',
                }}>
                    {/* Panel izquierdo — texto + CTA */}
                    <Box sx={{
                        position: 'relative',
                        width: '42%',
                        aspectRatio: '1/1',
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        px: { xs: 1, sm: 1, md: 3, lg: 4 },
                        py: { xs: 1, sm: 2, md: 4, lg: 6 },
                        userSelect: 'none',
                    }}>
                        {/* Ícono decorativo */}
                        {icon &&
                            <Box sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                zIndex: 0,
                            }}>
                                <img
                                    src={icon || ''}
                                    alt=""
                                    draggable={false}
                                    style={{
                                        width: 'auto',
                                        height: '100%',
                                        userSelect: 'none',
                                        pointerEvents: 'none'
                                    }}
                                />
                            </Box>
                        }

                        {/* Texto */}
                        <Box sx={{ position: 'relative', zIndex: 1, mb: { xs: 1, sm: 2, md: 3, lg: 4 }, textAlign: 'center' }}>
                            {title && (
                                <Typography variant="h1" sx={{
                                    color: 'white',
                                    mb: 3,
                                    lineHeight: { sm: themeSys.typography.h5.lineHeight, md: themeSys.typography.h2.lineHeight, lg: themeSys.typography.h1.lineHeight },
                                    letterSpacing: { sm: themeSys.typography.h5.letterSpacing, md: themeSys.typography.h2.letterSpacing, lg: themeSys.typography.h1.letterSpacing },
                                }}>
                                    {title}
                                </Typography>
                            )}
                            {subTitle && (
                                <Typography variant="h3" sx={{
                                    color: 'rgba(255,255,255,0.9)',
                                    mb: 2,
                                }}>
                                    {subTitle}
                                </Typography>
                            )}
                            {text && (
                                <Typography variant="body1" sx={{
                                    display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
                                    color: 'rgba(255,255,255,0.85)', textAlign: 'center', fontWeight: 100
                                }}>
                                    {text}
                                </Typography>
                            )}
                        </Box>

                        {/* CTAs */}
                        {components.length > 0 && (
                            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                {components.map((item: Component, idx: number) => (
                                    <ComponentLink key={idx} component={item} variant="cta" />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Panel derecho — imagen */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                        <img
                            src={bgMd || ''}
                            alt="Imagen"
                            draggable={false}
                            style={{
                                height: '100%',
                                width: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                userSelect: 'none',
                                pointerEvents: 'none'
                            }}
                        />
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export default ComponentCarrusel
